import { zodResolver } from "@hookform/resolvers/zod";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import { CountrySelect, RadioInput, SearchInput, SelectInput } from "@/shared/components";
import type { StepFormComponentProps, StepFormHandle } from "@/shared/stepper/types";

import {
  emirates,
  gccSpecOptions,
  newCarOptions,
  registeredUnderOptions,
} from "@/D2C/features/car-quote/common/static";
import { defaultVehicleFormValues } from "@/D2C/features/car-quote/redux/car-quote/carQuoteSlice";
import {
  getMotorSumInsuredRange,
  normalizeCurrencyInput,
} from "@/D2C/features/car-quote/utils/motorSumInsured";
import { App_Url_New } from "@/shared/common/static";
import DateInput from "@/shared/components/DateInput";
import { MotorMinMaxInput } from "@/shared/components/MotorMinMaxInput";
import { LOCALISATION_PROFILE } from "@/shared/config/localisation";
import { showToast } from "@/shared/lib/toast";
import { usePosterReducers } from "@/shared/redux/store/useGetData";
import { createVehicleDetailsSchema } from "@/shared/validation/carQuoteSchemas";
import { CarVehicleFormValues } from "../../redux/type";
import {
  getVehicleModelYears,
  getVehicles,
  getVehicleSpecifications,
} from "../../services/service";
import type { CarPersonalDetailsAutofillValues } from "../../utils/carPersonalDetailsOcr";

type VehicleOption = { id: string; label: string };

const readString = (item: Record<string, unknown> | string | number, keys: string[]) => {
  if (typeof item === "string" || typeof item === "number") return String(item);
  const value = keys.map((key) => item[key]).find((candidate) => candidate != null);
  return value == null ? "" : String(value);
};

export const VehicleDetailsStep = forwardRef<
  StepFormHandle<CarVehicleFormValues>,
  StepFormComponentProps<
    CarVehicleFormValues,
    {
      autofillValues?: CarPersonalDetailsAutofillValues;
      autofillVersion?: number;
      layoutClassName?: string;
    }
  >
>(function VehicleDetailsStep(
  { defaultValues, autofillValues, autofillVersion, layoutClassName },
  ref
) {
  const { motorSumInsured: motorSumInsuredState } = usePosterReducers();
  const motorSumInsuredRange = useMemo(
    () => getMotorSumInsuredRange(motorSumInsuredState.data),
    [motorSumInsuredState.data]
  );
  const vehicleDetailsSchema = useMemo(
    () => createVehicleDetailsSchema(motorSumInsuredRange),
    [motorSumInsuredRange]
  );
  const [vehicles, setVehicles] = useState<VehicleOption[]>([]);
  const [modelYearOptions, setModelYearOptions] = useState<VehicleOption[]>([]);
  const [vehicleVariantOptions, setVehicleVariantOptions] = useState<VehicleOption[]>([]);
  const form = useForm<CarVehicleFormValues>({
    resolver: zodResolver(vehicleDetailsSchema) as any,
    defaultValues: defaultValues ?? defaultVehicleFormValues,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const carModel = form.watch("carModel");
  const modelYear = form.watch("modelYear");
  const isNewCar = form.watch("isNewCar");
  const carValue = form.watch("carValue");
  const previousModelYearRef = useRef(modelYear);
  const previousCarModelRef = useRef(carModel);
  const vehicleVariant = form.watch("vehicleVariant");

  const shouldCallMinMaxApi = !!carModel && !!modelYear && !!vehicleVariant;

  useEffect(() => {
    form.reset(defaultValues ?? defaultVehicleFormValues);
  }, [defaultValues, form]);

  useEffect(() => {
    if (!autofillVersion || !autofillValues) {
      return;
    }

    if (autofillValues.carModel) {
      form.setValue("carModel", autofillValues.carModel, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    if (autofillValues.modelYear && /^\d{4}$/.test(autofillValues.modelYear)) {
      form.setValue("modelYear", autofillValues.modelYear, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    if (autofillValues.registeredEmirate) {
      form.setValue("registeredEmirate", autofillValues.registeredEmirate, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    if (autofillValues.carValue) {
      form.setValue("carValue", normalizeCurrencyInput(autofillValues.carValue), {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [autofillValues, autofillVersion, form]);

  useEffect(() => {
    let active = true;

    void getVehicles()
      .then((items) => {
        if (!active) return;
        setVehicles(
          items
            .map((item) => ({
              id: readString(item, ["id", "vehicle_id", "value"]),
              label: readString(item, ["label", "name", "display_name", "vehicle_name"]),
            }))
            .filter((item) => item.id && item.label)
        );
      })
      .catch(() => {
        if (active) showToast("Unable to load vehicles", "error");
      });

    return () => {
      active = false;
    };
  }, []);

  const carOptions = useMemo(
    () =>
      vehicles.map((item) => ({
        label: item.label,
        value: item.label,
        keywords: [item.id],
        meta: item,
      })),
    [vehicles]
  );

  const selectedVehicle = useMemo(
    () => vehicles.find((item) => item.label === carModel),
    [carModel, vehicles]
  );
  const carModelChanged = previousCarModelRef.current !== carModel;

  useEffect(() => {
    let active = true;
    setModelYearOptions([]);
    setVehicleVariantOptions([]);

    if (!selectedVehicle)
      return () => {
        active = false;
      };

    void getVehicleModelYears(selectedVehicle.id)
      .then((items) => {
        if (!active) return;
        setModelYearOptions(
          items
            .map((item) => {
              const year = readString(item, ["model_year", "year", "label", "value", "name"]);
              return { id: year, label: year };
            })
            .filter((item) => item.id)
        );
      })
      .catch(() => {
        if (active) showToast("Unable to load model years", "error");
      });

    return () => {
      active = false;
    };
  }, [selectedVehicle]);

  useEffect(() => {
    let active = true;
    setVehicleVariantOptions([]);

    if (!selectedVehicle || !modelYear || carModelChanged)
      return () => {
        active = false;
      };

    void getVehicleSpecifications(selectedVehicle.id, modelYear)
      .then((items) => {
        if (!active) return;
        setVehicleVariantOptions(
          items
            .map((item) => {
              const label = readString(item, [
                "label",
                "name",
                "specification",
                "vehicle_specs",
                "value",
              ]);
              return { id: readString(item, ["id", "specification_id", "value"]) || label, label };
            })
            .filter((item) => item.label)
        );
      })
      .catch(() => {
        if (active) showToast("Unable to load vehicle specifications", "error");
      });

    return () => {
      active = false;
    };
  }, [carModelChanged, modelYear, selectedVehicle]);

  useEffect(() => {
    if (previousCarModelRef.current && previousCarModelRef.current !== carModel) {
      form.setValue("modelYear", "");
      form.setValue("vehicleVariant", "");
    }
    previousCarModelRef.current = carModel;
  }, [carModel, form]);

  useEffect(() => {
    if (previousModelYearRef.current && previousModelYearRef.current !== modelYear) {
      form.setValue("vehicleVariant", "");
      form.setValue("registrationDate", "", { shouldValidate: false });
      form.clearErrors("registrationDate");
    }
    previousModelYearRef.current = modelYear;
  }, [form, modelYear]);

  useEffect(() => {
    const normalizedCarValue = normalizeCurrencyInput(carValue);

    if (carValue && carValue !== normalizedCarValue) {
      form.setValue("carValue", normalizedCarValue, { shouldValidate: true });
    }
  }, [carValue, form]);

  useEffect(() => {
    if (motorSumInsuredRange && form.getValues("carValue")) {
      void form.trigger("carValue");
    }
  }, [form, motorSumInsuredRange]);

  useEffect(() => {
    if (isNewCar) {
      return;
    }

    if (!modelYear || !/^\d{4}$/.test(modelYear)) {
      return;
    }

    const currentRegistrationDate = form.getValues("registrationDate");
    const currentDateMatch = currentRegistrationDate?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const currentYear = currentDateMatch?.[1];
    const currentMonth = currentDateMatch?.[2] || "01";
    const currentDay = currentDateMatch?.[3] || "01";

    if (currentYear === modelYear) {
      return;
    }

    form.setValue("registrationDate", `${modelYear}-${currentMonth}-${currentDay}`, {
      shouldValidate: true,
    });
  }, [form, isNewCar, modelYear]);

  useImperativeHandle(ref, () => ({
    submit: async () => form.trigger(undefined, { shouldFocus: true }),
    getValues: () => form.getValues(),
  }));

  const formatDate = (date: Date) => {
    return date.toISOString().split("T")[0];
  };

  const getRegistrationMinDate = () => {
    if (isNewCar) {
      return formatDate(new Date());
    }

    return modelYear ? `${modelYear}-01-01` : undefined;
  };

  const getRegistrationMaxDate = () => {
    const today = new Date();

    if (isNewCar) {
      const futureDate = new Date();
      futureDate.setDate(today.getDate() + 30);

      return formatDate(futureDate);
    }

    return formatDate(today);
  };

  const formLayoutClassName = layoutClassName ?? "grid grid-cols-1 gap-4 sm:grid-cols-2";
  const registrationRegionLabel = LOCALISATION_PROFILE.address.stateLabel.toLowerCase();

  return (
    <div className={formLayoutClassName}>
      <SearchInput
        control={form.control}
        name="carModel"
        label="What car do you drive?"
        placeholder="Search Make, Model and Trim"
        options={carOptions}
        required
      />

      <SelectInput
        control={form.control}
        name="modelYear"
        label="Select model year"
        placeholder="Select model year"
        options={modelYearOptions.map((item) => ({ label: item.label, value: item.id }))}
        searchable
        required
      />

      <SelectInput
        control={form.control}
        name="vehicleVariant"
        label="Can you help us identify it exactly?"
        placeholder="Search vehicle specs"
        options={vehicleVariantOptions.map((item) => ({
          label: item.label,
          value: item.label,
          keywords: [item.id],
        }))}
        required
      />

      <RadioInput
        control={form.control}
        name="gccSpec"
        label={LOCALISATION_PROFILE.motor.gccSpecificationLabel}
        options={gccSpecOptions}
        required
      />

      <RadioInput
        control={form.control}
        name="isNewCar"
        label="Is your car new?"
        options={newCarOptions}
        required
      />

      <CountrySelect
        control={form.control}
        name="registeredEmirate"
        label={
          isNewCar ? "Where would you like to register your car?" : "Where is your car registered?"
        }
        placeholder={`Select ${registrationRegionLabel}`}
        options={emirates}
        searchable
        required
      />

      <DateInput
        control={form.control}
        name="registrationDate"
        label={
          isNewCar ? "When would you like to register your car?" : "When was your car registered?"
        }
        min={getRegistrationMinDate()}
        max={getRegistrationMaxDate()}
        placeholder="DD/MM/YYYY"
        required
        yearOrder="desc"
      />

      <RadioInput
        control={form.control}
        name="registeredUnder"
        label="Is your car registered under:"
        options={registeredUnderOptions}
        required
      />

      <MotorMinMaxInput
        control={form.control}
        name="carValue"
        label={LOCALISATION_PROFILE.motor.vehicleValueLabel}
        placeholder={LOCALISATION_PROFILE.motor.vehicleValuePlaceholder}
        apiEndpoint={App_Url_New.ApiEndPoint.motorMinMax}
        required
        isCallingApi={shouldCallMinMaxApi}
      />
    </div>
  );
});
